import React, { useMemo, useState} from 'react';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbar } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../../hooks/useAuthenticatedData';
import config from '../../../../../config';
import { enterpriseGridOverrideTheme } from '../../../../../themes/EnterpriseGridTheme';
import MembershipPopup from '../../MembershipPopup';

function RepairsDepartmentNewVendorRFQDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);

  // Get the period parameter from the query string; default to 'MTD'
  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';

  // Construct the REST endpoint URL using the provided config and period
  const url = `${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/new-vendor-rfq-details?period=${period}`;

  const { data, isLoading, error } = useAuthenticatedData(url);

  // When a tab is clicked, update the query param in the URL.
  const handleTabChange = (event, newPeriod) => {
    navigate({
      pathname: location.pathname,
      search: `?period=${newPeriod}`
    });
  };

  // Process the REST data into rows for the DataGrid.
  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    // Extract the values and add unique IDs
    return data.map((item, index) => ({
      id: `${item.values.docno}-${item.values.userline}-${index}`, // Create unique ID
      vendorname: item.values.vendorname,
      buyer: item.values.buyer,
      docno: item.values.docno,
      userline: item.values.userline,
      addedon: item.values.addedon,
      partnumber: item.values.partnumber,
      description: item.values.description
    }));
  }, [data]);

  // Generate export filename: e.g. Repairs-MTD-new-vendor-rfq-details
  const generateExportFileName = () => {
    return `Repairs-${period}-new-vendor-rfq-details`;
  };

  // Define DataGrid columns based on the data structure.
  const columns = useMemo(
    () => [
      { field: 'vendorname', headerName: 'Vendor Name', width: 250 },
      { field: 'buyer', headerName: 'Buyer', width: 150 },
      { field: 'docno', headerName: 'Doc No', width: 120 },
      { field: 'userline', headerName: 'User Line', width: 100 },
      {
        field: 'addedon',
        headerName: 'Added On',
        width: 150,
        type: 'dateTime',
        valueFormatter: (value) => {
          if (!value) return '';
          return new Date(value).toLocaleDateString();
        },
      },
      { field: 'partnumber', headerName: 'Part Number', width: 150 },
      { field: 'description', headerName: 'Description', width: 200 },
    ],
    []
  );

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: '100%', mt: 4 }}>
      <Typography variant="h1" component="h1" gutterBottom align="left">
        Repairs Scorecard - New Vendor RFQ Details
      </Typography>

      {/* About the Data and Query Links Block */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        {/* Technical options: small links for debugging - Now Above */}
        <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>

          <Link
            sx={{ color: 'gray' }}
            component="button"
            variant="caption"
            onClick={() =>
              window.open(
                '/query-commander/editor?queryKey=repairs_dept_new_vendor_growth_summary',
                '_blank'
              )
            }
          >
            Summary Query
          </Link>
          <Link
            sx={{ color: 'gray' }}
            component="button"
            variant="caption"
            onClick={() =>
              window.open(
                '/query-commander/editor?queryKey=scorecards-supply-chain-repairs-department-scorecard-new-vendor-base-growth-rfq-details',
                '_blank'
              )
            }
          >
            Details Query
          </Link>
        </Box>

        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #1e1e1e, #2d2d2d)',
              border: '1px solid #424242',
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.5)',
              borderRadius: '12px',
            }}
          >
            <CardContent sx={{ p: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '.9rem',
                  mb: 0.5,
                }}
              >
                About the Data
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#b0b0b0',
                  fontSize: '0.75rem',
                }}
              >
               
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {/* Tabs for switching periods */}
        <Tabs
          value={period}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 2 }}
        >
          <Tab
            sx={{
              color: period === 'MTD' ? '#ffffff !important' : '#b3b3b3',
              fontWeight: period === 'MTD' ? 'bold' : 'normal',
            }}
            label="Month-to-Date"
            value="MTD"
          />
          <Tab
            sx={{
              color: period === 'QTD' ? '#ffffff !important' : '#b3b3b3',
              fontWeight: period === 'QTD' ? 'bold' : 'normal',
            }}
            label="Quarter-to-Date"
            value="QTD"
          />
          <Tab
            sx={{
              color: period === 'YTD' ? '#ffffff !important' : '#b3b3b3',
              fontWeight: period === 'YTD' ? 'bold' : 'normal',
            }}
            label="Year-to-Date"
            value="YTD"
          />
        </Tabs>
      </Box>

      <Paper elevation={3} sx={{ height: 400, width: '100%', p: 2 }}>
        <Box sx={{ height: '100%' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">Error: {error.message}</Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
              autoHeight={false}
              headerHeight={40}
              sx={enterpriseGridOverrideTheme}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                  csvOptions: { fileName: generateExportFileName() },
                  printOptions: { fileName: generateExportFileName() },
                },
              }}
            />
          )}
        </Box>

        {/* Pop-up dialogs for query links */}
        <MembershipPopup open={openMembership} onClose={() => setOpenMembership(false)} />
      </Paper>
    </Paper>
  );
}

export default RepairsDepartmentNewVendorRFQDetails;